/**
 * What the Contact Type field shows, decided without JSX so it can be tested on its own.
 *
 * Contact Type is the CRM's own classification (Keap: Lead / Client / Other, as the
 * account names them). It is not a CRM tag and not where the contact came from:
 *
 *   Contact Type  what kind of contact this is in the CRM
 *   CRM Tags      how the contact is organised
 *   Source        where ContactPilot found it
 *
 * Nothing here chooses a value. An existing contact's type is the CRM's record and is
 * shown, not edited; a new contact's type is a person's choice, from the CRM's own list.
 */

export interface ContactTypeOptionsLike {
  readonly supported: boolean;
  readonly required: boolean;
  readonly available: boolean;
  readonly options: ReadonlyArray<{ readonly id: string; readonly label: string }>;
}

export type ContactTypeView =
  /** The CRM has no contact type, or this person is not being created or matched. */
  | { readonly kind: 'hidden' }
  /** Already in the CRM: its type, as recorded there. */
  | { readonly kind: 'existing'; readonly label: string | null }
  /** New: a person chooses. `missing` blocks approval when the CRM needs a choice. */
  | { readonly kind: 'choose'; readonly missing: boolean; readonly unknownValue: string | null }
  /** New, but the CRM's list could not be read. Nothing can be chosen; approval waits. */
  | { readonly kind: 'unavailable' };

export function contactTypeView(input: {
  readonly types: ContactTypeOptionsLike | null | undefined;
  /** 'new' to be created; 'existing' already in the CRM; 'other' undecided or unknown. */
  readonly status: 'new' | 'existing' | 'other';
  readonly value: string | null | undefined;
  readonly existingValue?: string | null;
}): ContactTypeView {
  const t = input.types;
  if (!t || !t.supported) return { kind: 'hidden' };
  if (input.status === 'existing') {
    return { kind: 'existing', label: input.existingValue ? labelOf(t, input.existingValue) : null };
  }
  if (input.status !== 'new') return { kind: 'hidden' };
  if (!t.available) return { kind: 'unavailable' };
  const value = input.value ?? null;
  const known = value === null || t.options.some((o) => o.id === value);
  return { kind: 'choose', missing: t.required && (!value || !known), unknownValue: known ? null : value };
}

/** The CRM's own word for a type; an identifier it no longer lists is shown as it is. */
export function labelOf(t: ContactTypeOptionsLike, id: string): string {
  return t.options.find((o) => o.id === id)?.label ?? id;
}

/** Whether approval must wait on a contact type, over everyone being created. */
export function contactTypeBlocks(views: readonly ContactTypeView[]): string | null {
  if (views.some((v) => v.kind === 'unavailable')) {
    return 'Contact types could not be loaded from your CRM. Try again before approving.';
  }
  const missing = views.filter((v) => v.kind === 'choose' && v.missing).length;
  if (missing === 0) return null;
  return missing === 1
    ? 'Choose a Contact Type for the new contact before approving.'
    : `Choose a Contact Type for each of the ${missing} new contacts before approving.`;
}
